import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { computeScreenSizes } from '../../../util/hooks/useScreenSize';
import { getLayoutWidth } from '../../../util/layoutWidth';
import { resolveDeepLinkDialogs } from './BookingPanel.helpers';

export const SCROLL_LOCK_ID = 'ListingPage.MobileBookingPanel';

/**
 * Owns the open state of the booking dialogs, so it survives the layout switch:
 * the sheet (the booking form container of the bar) and the checkout (BookingModal).
 * Also handles the `?book=1` deep link, the scroll lock and closing on navigation.
 *
 * @param {object} params
 * @param {'bar' | 'sidebar'} params.variant
 * @param {boolean} params.autoOpen - Deep link asked to open the booking flow
 * @param {boolean} params.hasInitialDates
 * @param {boolean} params.scrollToTopOnOpen - Own or closed listing: scroll up when the sheet opens
 */
const useBookingDialogs = ({ variant, autoOpen, hasInitialDates, scrollToTopOnOpen }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Resolved in an effect with the real width: the first render uses the hydration width.
  useEffect(() => {
    const deepLink = resolveDeepLinkDialogs({
      autoOpen,
      hasDates: hasInitialDates,
      isDesktopLayout: computeScreenSizes(getLayoutWidth()).isDesktop,
    });
    setSheetOpen(deepLink.sheetOpen);
    setCheckoutOpen(deepLink.checkoutOpen);
    if (deepLink.sheetOpen && scrollToTopOnOpen) {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollLocked = variant === 'bar' && sheetOpen;
  useEffect(() => {
    dispatch(manageDisableScrolling(SCROLL_LOCK_ID, scrollLocked));
    return () => {
      dispatch(manageDisableScrolling(SCROLL_LOCK_ID, false));
    };
  }, [dispatch, scrollLocked]);

  // Only a real URL change closes the dialogs, not the first render.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setSheetOpen(false);
      setCheckoutOpen(false);
    }
  }, [location.pathname]);

  const openSheet = useCallback(() => {
    setSheetOpen(true);
    if (scrollToTopOnOpen) {
      window.scrollTo(0, 0);
    }
  }, [scrollToTopOnOpen]);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const openCheckout = useCallback(() => setCheckoutOpen(true), []);
  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);

  return { sheetOpen, openSheet, closeSheet, checkoutOpen, openCheckout, closeCheckout };
};

export default useBookingDialogs;
